import { default as React, useEffect, useState, useRef } from "react";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { useSelector } from "react-redux";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import _, { assignWith } from "lodash";
import { icon, label, number } from "../../../config";
import { filterData } from "../../../utils/kendo";
import { DrutasDropdown } from "../../DrutasDropdown/DrutasDropdown";
import { DrutasDropdownFlowElements } from "../../DrutasDropdown/DrutasDropdownFlowElements";
import { getDD4List } from "../../TaskSidebar/TaskSidebarContent/Details/taskDetail.helper";
import { getAssigneeData, handleGetProjectWorkflows } from "../../TaskSidebar/sidebar.helper";
import { handleWorkflowAssignment, itemRenderAssignId, valueRenderAssignId } from "../workflowHelper";

const editField = "inEdit";
/**
 * In the flow elements this dropdown is used to set WorkflowAssignedId
 * Parent - DetailedFlowElementDetails
 * @author Sarthak Arora, Muskan Thakur
 * @param {*} props
 */
const FlowElementsWorkflowAssignedId = (props) => {
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);
  const [assignedWorkflowList, setAssignedWorkflowList] = useState([]);
  const [filteredAssignedWorkflowList, setFilteredAssignedWorkflowList] = useState(null);
  const [filterText, setFilterText] = useState("");
  const { currentTheme } = useThemeSwitcher();
  const userHasManuallySelected = useRef(false);

  const {
    flowWorkflowAssignmentId,
    setFlowWorkflowAssignmentId,
    flowWorkflowAssignmentType,
    flowTaskAssignmentId,
    flowAssignmentType,
    setFlowWorkflowAssignmentType,
    workflowAssignmentTypes,
  } = props;

  /**
   * sets workflow assignment type for new and existing flow element
   */
  useEffect(() => {
    if (!props?.dataItem?.[editField]) {
      const requiredWorkflowAssignType = workflowAssignmentTypes?.find((c) => c.value === props?.dataItem?.workflowAssignmentType);
      setFlowWorkflowAssignmentType(requiredWorkflowAssignType);
    }
  }, [props?.dataItem?.workflowAssignmentType, workflowAssignmentTypes]);

  useEffect(() => {
    if (flowTaskAssignmentId) {
      setFlowWorkflowAssignmentId(null);
      getAndFilterAssignmentList();
      userHasManuallySelected.current = false;
    } else {
      /** Clear workflow assignment when task assignment is cleared */
      setFlowWorkflowAssignmentId(null);
      setAssignedWorkflowList([]);
      setFilteredAssignedWorkflowList(null);
      userHasManuallySelected.current = false;
    }
  }, [flowTaskAssignmentId]);

  /**
   * Auto-selects workflow from list based on priority: logged-in user > default workflow > first workflow
   * @author Muskan Thakur
   */
  const autoSelectWorkflow = (list, workflowAssignmentType) => {
    if (!list || list.length === 0) {
      setFlowWorkflowAssignmentId(null);
      return;
    }

    /** Priority 1: Check if logged-in user is in the list */
    const currentUserInList = list.find((c) => c.value === user?.id && c.RelatedAssignmentType === number.TWO);
    if (currentUserInList) {
      const userAssignmentType = workflowAssignmentTypes?.find((type) => type.value === number.TWO);
      if (userAssignmentType) {
        setFlowWorkflowAssignmentType(userAssignmentType);
      }
      setFlowWorkflowAssignmentId(currentUserInList);
      return;
    }

    /** Priority 2: Find default workflow */
    const defaultWorkflow = list.find((item) => item.isDefault === true);
    if (defaultWorkflow) {
      setFlowWorkflowAssignmentId(defaultWorkflow);
      return;
    }

    /** Priority 3: Fallback to first workflow or handle workflow assignment */
    if (workflowAssignmentType === number.TWO) {
      handleWorkflowAssignment(
        flowTaskAssignmentId,
        flowTaskAssignmentId?.AssignmentType,
        setFlowWorkflowAssignmentType,
        setFlowWorkflowAssignmentId,
        null
      );
    } else {
      const firstItem = list[number.ZERO];
      if (firstItem) {
        setFlowWorkflowAssignmentId(firstItem);
      }
    }
  };

  /**
   * Validates current value when list changes - clears if not in list
   * This runs separately to avoid interfering with user selections
   * @author Muskan Thakur
   */
  useEffect(() => {
    if (!flowTaskAssignmentId || !assignedWorkflowList || assignedWorkflowList.length === 0) {
      return;
    }


    /** Don't validate/clear if user has manually selected Unassigned */
    if (userHasManuallySelected.current && !flowWorkflowAssignmentId?.value) {
      return;
    }

    /** Validate current value - if it exists but is not in the list, clear it */
    if (flowWorkflowAssignmentId?.value && !assignedWorkflowList.find((item) => item.value === flowWorkflowAssignmentId.value)) {
      setFlowWorkflowAssignmentId(null);
      /** After clearing, trigger auto-selection by updating a dependency */
      /** This will cause the main effect to run and auto-select */
    }
  }, [assignedWorkflowList, flowTaskAssignmentId, flowWorkflowAssignmentId, setFlowWorkflowAssignmentId]);

  /**
   * Sets workflow assignment Id for new and existing flow element
   * Auto selects first workflow
   * @author Sarthak Arora, Muskan Thakur
   */
  useEffect(() => {
    /** If flowTaskAssignmentId is null, clear workflow assignment */
    if (!flowTaskAssignmentId) {
      setFlowWorkflowAssignmentId(null);
      return;
    }

    /** If list is empty, clear the value to show Unassigned */
    if (!assignedWorkflowList || assignedWorkflowList.length === 0) {
      setFlowWorkflowAssignmentId(null);
      return;
    }

    /** Don't auto-select if user has manually selected a value (including Unassigned) */
    if (userHasManuallySelected.current) {
      return;
    }

    const { workflowAssignmentId, workflowAssignmentType } = props?.dataItem || {};
    const isEditMode = props?.dataItem?.[editField];
    const isExistingFlowElement = !!props?.dataItem?.id ;

    /** For existing flow elements (not in edit mode), find the workflow from dataItem */
    if (!isEditMode) {
      const requiredWorkflow = assignedWorkflowList.find((c) => c.value === workflowAssignmentId);
      setFlowWorkflowAssignmentId(requiredWorkflow || (workflowAssignmentId ? { label: label.UNASSIGNED } : null));
      return;
    }

    /** For editing existing flow elements (in edit mode) */
    const hasCurrentValue = !!flowWorkflowAssignmentId?.value;
    
    if (isExistingFlowElement && (workflowAssignmentId === null || workflowAssignmentId === undefined)) {
      setFlowWorkflowAssignmentId(null);
      return;
    }
    
    /** If workflowAssignmentId exists, try to find it in the list */
    if (workflowAssignmentId) {
      const requiredWorkflowAssignmentId = assignedWorkflowList.find((item) => item.value === workflowAssignmentId);
      if (requiredWorkflowAssignmentId) {
        /** Found the saved workflow in the list - use it if no current value or if it matches saved */
        if (!hasCurrentValue || flowWorkflowAssignmentId?.value === workflowAssignmentId) {
          setFlowWorkflowAssignmentId(requiredWorkflowAssignmentId);
        }
        /** If current value exists and doesn't match saved, keep user's selection */
      } else {
        /** Saved workflow not found in current list (user/project changed) - always auto-select */
        const assignmentTypeToUse = flowWorkflowAssignmentType?.value || workflowAssignmentType;
        autoSelectWorkflow(assignedWorkflowList, assignmentTypeToUse);
      }
    } else {
      /** No saved workflowAssignmentId - only auto-select for new elements, not existing ones */
      if (!isExistingFlowElement && !hasCurrentValue) {
        const assignmentTypeToUse = flowWorkflowAssignmentType?.value || workflowAssignmentType;
        autoSelectWorkflow(assignedWorkflowList, assignmentTypeToUse);
      } else if (isExistingFlowElement && !hasCurrentValue) {
        setFlowWorkflowAssignmentId(null);
      }
    }
  }, [props.dataItem?.workflowAssignmentId, props.dataItem?.workflowAssignmentType, props.dataItem?.id, props.dataItem?.[editField], assignedWorkflowList, flowTaskAssignmentId, flowWorkflowAssignmentId, user?.id, workflowAssignmentTypes, setFlowWorkflowAssignmentId, setFlowWorkflowAssignmentType]);

  /**
   * Returns OR related assignee list based on related assignment type
   * @param {Object} e
   * @returns {Void}
   * @author Himanshu Negi
   */
  const getAndFilterAssignmentList = async (event) => {
    setFilterText(event?.target?.value ? event?.target?.value : "");
    const payload = { assignedId: flowTaskAssignmentId, defaultDetails };
    let relatedAssignedIdList = await getDD4List(payload);
    const customFilter = event
      ? {
          logic: "or",
          filters: [
            { field: "Email", operator: "contains", value: event.target.value },
            { field: "label", operator: "contains", value: event.target.value },
          ],
        }
      : null;
    relatedAssignedIdList = event ? filterData(customFilter, relatedAssignedIdList) : relatedAssignedIdList;
    if (event?.target?.value) setFilteredAssignedWorkflowList(relatedAssignedIdList);
    else {
      setAssignedWorkflowList(relatedAssignedIdList);
      clearFilterText();
    }
  };

  /**
   * sets workflow assignment Id (workflow Id or project Id) based on selection
   * @author Muskan Thakur
   */
  const handleWorkflowChange = async (event) => {
    userHasManuallySelected.current = true;
    
    if (event) {
      const selectedAssignId = event;
      setFlowWorkflowAssignmentId(selectedAssignId);
      const relatedType = defaultDetails?.relatedAssignmentTypeList?.find((type) => type.value === selectedAssignId?.RelatedAssignmentType);
      setFlowWorkflowAssignmentType(relatedType);
    } else {
      /** User explicitly selected Unassigned (null) */
      setFlowWorkflowAssignmentId(null);
    }
  };

  /**
   * Custom dropdown item render for workflow list
   * @returns JSX
   * @author Sarthak Arora
   */

  const filtersContent = () => {
    return (
      <div className='p-3 pb-2'>
        <div className='assignto-search position-relative'>
          <span className='assignto-search-icon'>{icon.SEARCH}</span>
          <input
            autoFocus={true}
            className='assignto-search-field form-control w-100'
            placeholder='Search'
            value={filterText}
            onChange={getAndFilterAssignmentList}
          />
        </div>
      </div>
    );
  };

  const itemRenderNew = (item, handleOnClick, handleElementMouseDown) => {
    return (
      <>
        {item?.RelatedAssignmentType === number.TWO ? (
          <div
            className='d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text'
            onClick={() => handleOnClick(item)}
            onMouseDown={handleElementMouseDown}>
            <div className='profile-picture-container mr-3'>
              {item?.PhotoLinks?.small ? <img src={item?.PhotoLinks?.small} className='profile-picture' /> : <span>{icon.USER_ICON}</span>}
            </div>
            <div className='truncate-text'>
              <div className='assignee-name truncate-text'>{item?.label}</div>
              <div className='assignee-email truncate-text'>{item?.Email}</div>
            </div>
          </div>
        ) : (
          <div
            className='d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text'
            onClick={() => handleOnClick(item)}
            onMouseDown={handleElementMouseDown}>
            <div className={`profile-picture-container mr-3 ${item?.privacy == number.ONE ? "black-icon" : item?.privacy == number.TWO ? "sky-blue-icon" : item?.privacy == number.THREE ? "maroon" : item?.privacy == number.FOUR ? "sky-blue-icon" : "black-icon"}`}>
              {icon[item?.icon] ? icon[item?.icon] : ""}
            </div>
            <div className='truncate-text d-flex align-items-center'>
              <div className='assignee-name truncate-text'>{item?.label}</div>
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * Render dropdown value
   * @author Sarthak Arora, Muskan Thakur
   */
  const valueRenderNew = (item) => {
    /** Handle unassigned state - show Unassigned with user icon */
    if (!item || !flowTaskAssignmentId) {
      return (
        <div className='d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text'>
          <div className='d-flex align-items-center truncate-text'>
            <div className='image-box mr-3'>
              <span className='assignee-icon'>{icon.USER_ICON}</span>
            </div>
            <div className='truncate-text'>
              <div className='assignee-name truncate-text'>{label.UNASSIGNED}</div>
            </div>
          </div>
          <span>{icon.ARROW_DROP_DOWN_ICON}</span>
        </div>
      );
    }

    return (
      <>
        {item?.RelatedAssignmentType === number.TWO ? (
          <div className='d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text'>
            <div className='d-flex align-items-center'>
              <div className='image-box mr-3'>{item?.PhotoLinks?.small ? <img src={item?.PhotoLinks?.small} /> : <span>{icon.USER_ICON}</span>}</div>
              <div className='truncate-text'>
                <div className='assignee-name truncate-text'>{item?.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
        ) : (
          <div className='d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text'>
            <div className='d-flex align-items-center truncate-text'>
              <div className={`image-box mr-3 ${item?.privacy == number.ONE ? "black-icon" : item?.privacy == number.TWO ? "sky-blue-icon" : item?.privacy == number.THREE ? "maroon" : item?.privacy == number.FOUR ? "sky-blue-icon" : "black-icon"}`}>
                <span className='assignee-icon'>{icon[item?.icon] ? icon[item?.icon] : ""}</span>
              </div>
              <div className='truncate-text'>
                <div className='assignee-name truncate-text'>{item?.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
        )}
      </>
    );
  };

  /**
   * Clear filter text
   * @author Muskan Thakur
   */
  const clearFilterText = () => {
    setFilteredAssignedWorkflowList(null);
  };

  /** Set value to Unassigned object when flowTaskAssignmentId is null */
  /** Only show Unassigned when flowTaskAssignmentId is null, not when list is loading */
  const displayValue = !flowTaskAssignmentId ? { label: label.UNASSIGNED } : flowWorkflowAssignmentId;
  /** Set title to Unassigned when no workflow is selected or no assignee is selected */
  const tooltipTitle = !flowTaskAssignmentId || !displayValue || displayValue?.label === label.UNASSIGNED 
    ? label.UNASSIGNED 
    : displayValue?.label;

  return (
    <React.Fragment>
      <span title={tooltipTitle}>
        <DrutasDropdownFlowElements
          value={displayValue}
          onChange={handleWorkflowChange}
          data={filteredAssignedWorkflowList || assignedWorkflowList}
          label={"label"}
          key={"value"}
          isfilter={true}
          filterContent={filtersContent}
          itemRender={itemRenderNew}
          valueRender={valueRenderNew}
          disabled={!props?.dataItem?.[editField] || !flowTaskAssignmentId}
          popupClass='dd2-height'
          className={"flow-elements-dd dd2-height "}
          clearFilterText={clearFilterText}
        />
      </span>
    </React.Fragment>
  );
};

export default FlowElementsWorkflowAssignedId;
